import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DealerAuthProvider } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerLayout from '../components/DealerLayout';
import { fetchDealerOrders } from '@/lib/api/orders';
import type { DealerOrder } from '@/mocks/dealerOrders';
import useScrollLock from '@/hooks/useScrollLock';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-green-500/20 text-green-400',
  shipped: 'bg-[#F97316]/20 text-[#F97316]',
};

function OrderDetailDrawer({ order, onClose }: { order: DealerOrder; onClose: () => void }) {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  useScrollLock();
  const drawerBg = dark ? 'bg-[#1C2B3A]' : 'bg-white';
  const borderB = dark ? 'border-white/10' : 'border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/40' : 'text-gray-400';
  const innerBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-50';
  const closeBtn = dark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/50" onClick={onClose}></div>
      <div className={`w-full max-w-md h-full overflow-y-auto flex flex-col ${drawerBg}`}>
        <div className={`flex items-center justify-between px-6 py-4 border-b shrink-0 ${borderB}`}>
          <div>
            <p className={`font-black text-base ${text}`}>{order.id}</p>
            <p className={`text-xs mt-0.5 ${muted}`}>{order.createdAt}</p>
          </div>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${closeBtn}`}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 p-6 space-y-6">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${muted}`}>Status</p>
            <span className={`inline-block text-sm font-bold px-4 py-1.5 rounded-full capitalize ${statusColors[order.status]}`}>
              {order.status}
            </span>
          </div>

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${muted}`}>Order Items</p>
            <div className="space-y-2">
              {order.items.map((item, i) => (
                <div key={i} className={`rounded-lg px-4 py-3 flex items-center justify-between ${innerBg}`}>
                  <div>
                    <p className={`text-sm font-semibold ${text}`}>{item.productName}</p>
                    <p className={`text-xs mt-0.5 capitalize ${muted}`}>{item.category} &bull; ${item.unitPrice.toLocaleString()} each</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs ${muted}`}>Qty</p>
                    <p className={`font-black text-lg ${text}`}>{item.qty}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-lg px-4 py-3 flex items-center justify-between ${innerBg}`}>
            <p className={`text-sm font-semibold ${muted}`}>Order Total</p>
            <p className="text-[#F97316] text-xl font-black">${order.total.toLocaleString()}</p>
          </div>

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${muted}`}>Shipping Address</p>
            <p className={`text-sm leading-relaxed ${dark ? 'text-white/70' : 'text-gray-600'}`}>{order.shippingAddress}</p>
          </div>

          {order.notes && (
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${muted}`}>Notes</p>
              <p className={`text-sm leading-relaxed italic ${dark ? 'text-white/70' : 'text-gray-600'}`}>&ldquo;{order.notes}&rdquo;</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 shrink-0">
          <Link
            to="/dealer/new-order"
            className="block w-full text-center bg-[#F97316] text-white font-bold py-3 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            Reorder Same Items
          </Link>
        </div>
      </div>
    </div>
  );
}

function OrdersContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'shipped'>('all');
  const [selectedOrder, setSelectedOrder] = useState<DealerOrder | null>(null);
  const [orders, setOrders] = useState<DealerOrder[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const nextOrders = await fetchDealerOrders();
        setOrders(nextOrders);
      } catch {
        setOrders([]);
      }
    };

    void load();
  }, []);

  const card = dark ? 'bg-[#1C2B3A] border-white/5' : 'bg-white border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-400';
  const faint = dark ? 'text-white/40' : 'text-gray-400';
  const headerText = dark ? 'text-white/40' : 'text-gray-400';
  const borderB = dark ? 'border-white/10' : 'border-gray-200';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const rowHover = dark ? 'hover:bg-white/3' : 'hover:bg-gray-50';
  const inactiveFilter = dark ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200';
  const eyeBtn = dark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100';
  const emptyText = dark ? 'text-white/30' : 'text-gray-300';

  const filtered = filter === 'all' ? orders : orders.filter((order) => order.status === filter);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {(['all', 'pending', 'confirmed', 'shipped'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                filter === status ? 'bg-[#F97316] text-white' : inactiveFilter
              }`}
            >
              {status === 'all' ? 'All Orders' : status}
              <span className="ml-1.5 opacity-60">
                ({status === 'all' ? orders.length : orders.filter((order) => order.status === status).length})
              </span>
            </button>
          ))}
        </div>
        <Link
          to="/dealer/new-order"
          className="flex items-center gap-2 bg-[#F97316] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </div>
          New Order
        </Link>
      </div>

      <div className={`border rounded-lg overflow-hidden ${card}`}>
        <div className={`grid grid-cols-12 gap-3 px-5 py-3 text-xs font-bold uppercase tracking-wide border-b ${headerText} ${borderB}`}>
          <div className="col-span-2">Order ID</div>
          <div className="col-span-4">Items</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2 text-center">Status</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1"></div>
        </div>

        <div className={`divide-y ${divider}`}>
          {filtered.length === 0 ? (
            <div className={`text-center py-16 ${emptyText}`}>
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-sm">No orders found</p>
            </div>
          ) : (
            filtered.map((order) => (
              <div key={order.id} className={`grid grid-cols-12 gap-3 px-5 py-4 items-center transition-colors ${rowHover}`}>
                <div className="col-span-2">
                  <p className={`text-sm font-bold ${text}`}>{order.id}</p>
                </div>
                <div className="col-span-4">
                  <p className={`text-xs leading-relaxed ${muted}`}>
                    {order.items.map((item) => `${item.productName.replace('Stingray ', '')} ×${item.qty}`).join(', ')}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className={`text-xs ${faint}`}>{order.createdAt}</p>
                </div>
                <div className="col-span-2 flex justify-center">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[order.status]}`}>
                    {order.status}
                  </span>
                </div>
                <div className="col-span-1 text-right">
                  <p className="text-[#F97316] font-black text-sm">${order.total.toLocaleString()}</p>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${eyeBtn}`}
                    title="View details"
                  >
                    <i className="ri-eye-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selectedOrder && (
        <OrderDetailDrawer order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}

export default function DealerOrdersPage() {
  return (
    <DealerAuthProvider>
      <DealerLayout title="My Orders">
        <OrdersContent />
      </DealerLayout>
    </DealerAuthProvider>
  );
}
